import React from "react";
import {
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Typography,
    TextField,
    InputAdornment,
    Paper,
    useTheme,
    Alert,
} from "@mui/material";
import { Cancel01Icon, StarIcon, Invoice01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAppSelector } from "@/hooks";
import { useAddPointsMutation } from "@/api/loyaltyApi";
import useSnackbar from "@/hooks/useSnackbar";
import { activeOrganization } from "@/features/organizations/slice";
import * as Yup from "yup";
import { useFormik } from "formik";
import ClientAvatar from "@/components/ClientAvatar";

interface Props {
    open: boolean;
    onClose: () => void;
    client: any;
}

const AddClientPointsDialog: React.FC<Props> = ({ open, onClose, client }) => {
    const { palette } = useTheme();
    const organization = useAppSelector(activeOrganization);
    const auth = useAppSelector((state) => state.auth);

    const [addPoints, { isLoading }] = useAddPointsMutation();
    const [openSnackbar] = useSnackbar();

    const loyaltyProgram = organization?.settings?.loyaltyProgram;
    const pointsPerAmount = loyaltyProgram?.pointsPerAmount || 1;
    const includeTax = organization?.settings?.includeTax ?? true;

    const validationSchema = Yup.object().shape({
        amount: Yup.number()
            .transform((value) => (isNaN(value) ? undefined : value))
            .positive("El monto debe ser mayor a 0")
            .required("Campo requerido"),
        reference: Yup.string().required("Campo requerido"),
    });

    const formik = useFormik({
        initialValues: {
            amount: 0,
            reference: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            const pointsToEarn = Math.floor(values.amount / pointsPerAmount);

            if (pointsToEarn <= 0) {
                openSnackbar("Monto insuficiente para ganar puntos", "warning");
                return;
            }

            const response = await addPoints({
                organizationId: organization?.id || "",
                uid: client.id,
                phoneNumber: client.phone,
                points: pointsToEarn,
                subtotal: Number(values.amount),
                reference: values.reference,
                createdBy: auth.user?.uid || "",
            });

            if (!(response as any).error) {
                openSnackbar("Puntos agregados correctamente", "success");
                formik.resetForm();
                onClose();
            } else {
                openSnackbar("Error al agregar puntos", "error");
            }
        },
    });

    const pointsToEarn = Math.floor((formik.values.amount || 0) / pointsPerAmount);

    // Helper text based on organization settings
    const helperTextBase = `Monto base sin impuestos${includeTax ? ' (IVA no incluido)' : ''}`;

    return (
        <Dialog
            open={open}
            onClose={() => {
                formik.resetForm();
                onClose();
            }}
            fullWidth
            maxWidth="xs"
            PaperProps={{
                sx: {
                    borderRadius: 8,
                }
            }}
        >
            <DialogContent sx={{ p: 4 }}>
                <Stack direction="row" justifyContent="flex-end">
                    <IconButton
                        onClick={() => {
                            formik.resetForm();
                            onClose();
                        }}
                        component={Paper}
                        elevation={2}
                        size="large"
                        sx={{
                            bgcolor: 'white',
                        }}
                    >
                        <HugeiconsIcon icon={Cancel01Icon} size={24} color={palette.primary.main} />
                    </IconButton>
                </Stack>
                <Stack spacing={3}>
                    <ClientAvatar client={client} size={100} />

                    {formik.submitCount > 0 && !formik.isValid && (
                        <Alert severity="error">Corrija los siguientes errores para continuar</Alert>
                    )}

                    <Stack spacing={2} width="100%">
                        <TextField
                            fullWidth
                            id="amount"
                            name="amount"
                            label="Monto facturado"
                            type="number"
                            value={formik.values.amount}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.amount && Boolean(formik.errors.amount)}
                            helperText={(formik.touched.amount && formik.errors.amount) || helperTextBase}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <HugeiconsIcon icon={StarIcon} size={24} color={palette.primary.main} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            id="reference"
                            name="reference"
                            label="Referencia"
                            value={formik.values.reference}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.reference && Boolean(formik.errors.reference)}
                            helperText={formik.touched.reference && formik.errors.reference}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <HugeiconsIcon icon={Invoice01Icon} size={24} color={palette.primary.main} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
                            <Typography variant="body1" color="text.secondary">Total a agregar</Typography>
                            <Typography variant="body2">+{pointsToEarn}pts</Typography>
                        </Stack>
                    </Stack>

                    <LoadingButton
                        fullWidth
                        color="success"
                        variant="contained"
                        size="large"
                        loading={isLoading}
                        onClick={() => formik.handleSubmit()}
                    >
                        Agregar Puntos
                    </LoadingButton>
                </Stack>
            </DialogContent>
        </Dialog >
    );
};

export default AddClientPointsDialog;
